import React, { Fragment, useState, useEffect, useRef, memo } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardHeader, Typography, Divider, MenuItem, TextField } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { timeConversion, eventService, alertService } from "client-app-core";
import Close from "@mui/icons-material/Close";
import isArray from "lodash/isArray";
import uniq from "lodash/uniq";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import AlarmSummaryWidget from "../AlarmSummaryWidget/AlarmSummaryWidget";
import { Translate, getTranslation } from "orion-components/i18n";
import { TargetingIcon } from "orion-components/SharedComponents";
import { contextById } from "orion-components/ContextualData/Selectors";
import { fullscreenCameraOpen } from "orion-components/AppState/Selectors";
import EventBus from "orion-components/SharedComponents/EventBus";
import {
	selectAlertOccurrences,
	selectAggregateAlertCountById,
	selectAllowAlertConcurrence,
	selectIsAlertConcurrenceEnabled
} from "../../../GlobalData/Selectors";

import {
	PinnedItemsWidget,
	Activities,
	FileWidget,
	CamerasWidget,
	CardHolderWidget,
	SensorOPDetectionWidget,
	ListWidget,
	DynamicKVPWidget
} from "../index";
import * as actionCreators from "../../AlertProfile/actions";
import { acknowledgeNotifications, unAcknowledgeNotifications } from "orion-components/Dock/Notifications/actions";
import { acknowledgeAlerts, unAcknowledgeAlerts } from "orion-components/Dock/Alerts/actions";
import AccessControlWidget from "../AccessControl/AccessControlWidget";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { getEntityIconsByType } from "orion-components/GlobalData/Selectors";
import { makeStyles } from "@mui/styles";
import CameraClusters from "../../Widgets/CameraClusters/CameraClusters";
import AlertActionsWidget from "../AlertActions/AlertActionsWidget";
import { streamIndividualEntityByType } from "../../../ContextPanel/ContextPanelData/actions";
import { unsubscribe, unsubscribeFromContext, startListStream } from "../../../ContextualData/Actions";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";
import moment from "moment";
import { userIntegrationByFeedIdSelector } from "orion-components/Session/Selectors";
const jsonata = require("jsonata");
import { canManageByApplication } from "orion-components/Profiles/Selectors";

import { styled } from "@mui/material/styles";

const CustomizedTextField = styled(TextField)(({ height }) => ({
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			border: "none",
			borderBottom: "1px solid #5E6571",
			borderRadius: 0
		},
		"&:hover fieldset": {
			borderBottom: "1px solid #4DB5F4",
			borderRadius: 0
		},
		"&.Mui-focused fieldset": {
			borderBottom: "1px solid #4DB5F4",
			borderRadius: 0
		},
		"&.Mui-disabled fieldset": {
			borderBottom: "1px solid #5E657180",
			borderRadius: 0
		},
		"& .MuiInputBase-input": {
			fontSize: "11px",
			color: "#FFFFFF",
			textAlign: "left",
			fontWeight: "normal",
			fontFamily: "Roboto, sans-serif",
			letterSpacing: "0px",
			opacity: 1,
			height: height,
			paddingTop: 0,
			paddingBottom: 0,
			paddingLeft: 0,
			paddingRight: 32,
			boxSizing: "border-box",
			display: "flex",
			alignItems: "center"
		},
		"& .MuiSelect-select": {
			height: height,
			paddingTop: 0,
			paddingBottom: 0,
			paddingLeft: 0,
			display: "flex",
			alignItems: "center",
			textAlign: "left"
		},
		"& .MuiSelect-icon": {
			color: "#FFFFFF"
		}
	},
	"& .MuiInputBase-root": {
		height: height,
		minHeight: height,
		boxSizing: "border-box"
	}
}));

const propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	readOnly: PropTypes.bool,
	forReplay: PropTypes.bool,
	endDate: PropTypes.date,
	activityId: PropTypes.string,
	closeDetailView: PropTypes.func,
	timeFormat: PropTypes.string,
	selectFloorPlanOn: PropTypes.func,
	floorPlansWithFacilityFeed: PropTypes.object,
	notificationId: PropTypes.string
};
const defaultProps = {
	closeDetailView: () => {},
	timeFormat: "12-hour",
	selectFloorPlanOn: () => {},
	floorPlansWithFacilityFeed: null
};
const useStyles = makeStyles({
	avatar: {
		marginLeft: 16
	},
	cardHeaderForExpanded: {
		marginTop: 0
	}
});
let alertCameraIds = {};

const AlertPanelDetailWidget = props => {
	const {
		readOnly,
		forReplay,
		endDate,
		notificationId,
		closeDetailView,
		selectFloorPlanOn,
		floorPlansWithFacilityFeed,
		dir,
		alertProfileSlideout,
		isExpanded,
		disableCloseIcon
	} = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const { loadProfile, closeNotification } = actionCreators;
	const user = useSelector(state => state.session.user);
	const allowAlertConcurrence = useSelector(selectAllowAlertConcurrence);
	const isBottomDock = useSelector(state => state.appState.persisted.isBottomDock);
	const isAutoAckDisabled = useSelector(state => getEcoSettingByKey("alertDisableAckOnProfileOpen")(state));

	// Values from Redux state
	const context = useSelector(state => contextById(notificationId)(state));
	const selectedContext = context;
	const timeFormat = useSelector(state => state.appState.global?.timeFormat);
	const fullscreenCamera = useSelector(state => fullscreenCameraOpen(state));
	const locale = useSelector(state => state.i18n.locale);
	const appId = useSelector(state => state.appId);
	const eventLookups = useSelector(state => state.eventLookups);
	const colors = useSelector(state => getEntityIconsByType(state)("frame_colors"));
	const disableAlertUnack = useSelector(state => getEcoSettingByKey("disableAlertUnack")(state) === true);
	const aggregateChildSortOrder =
		useSelector(state => getEcoSettingByKey("aggregateAlertChildSortOrder")(state)) || "oldest"; // Allowed values: "oldest" | "newest"
	// Default: "oldest"
	const canManageEvent = useSelector(state =>
		state.session.user.profile.applications
			?.find(app => app.appId === "events-app")
			?.permissions?.includes("manage")
	);
	const canManageLists = useSelector(state => canManageByApplication(state, "lists-app", "manage"));
	const [isResponding, setIsResponding] = useState(false);
	const activityObjectFeed = useSelector(state =>
		userIntegrationByFeedIdSelector(context?.entity?.activity?.object?.feedId)(state)
	);
	const alertCount = useSelector(selectAggregateAlertCountById(notificationId));
	const occurrences = useSelector(selectAlertOccurrences(notificationId));
	const orderedOccurrences = React.useMemo(() => {
		if (!occurrences?.length) return [];
		return aggregateChildSortOrder === "newest" ? [...occurrences].reverse() : occurrences;
	}, [occurrences, aggregateChildSortOrder]);
	const [alert, setAlert] = useState({
		...(context?.entity || {})
	});
	const [selectedOccurrenceId, setSelectedOccurrenceId] = useState(occurrences?.[0]?.id || "");
	const selectedOccurrence = occurrences?.find(occ => occ.id === selectedOccurrenceId) || occurrences?.[0];
	const [eventEnabled, setEventEnabled] = useState(false);
	const [devicePointWithControls, setDeviceWithControls] = useState(null);
	const [daysPassed, setDaysPassed] = useState(0);
	const [hoursPassed, setHoursPassed] = useState(0);
	const [minutesPassed, setMinutesPassed] = useState(0);
	const [canEscalateEvent, setCanEscalateEvent] = useState(false);
	const [canEscalateAlert, setCanEscalateAlert] = useState(false);
	const [canRespondToConcurrence, setCanRespondToConcurrence] = useState(false);

	// Local variables
	const { entity = {}, camerasInRange = [] } = context || {};
	const { id: activityId, activityDate, object, target } = entity?.activity || {};
	const { geometry, createdDate, eventTypeId, actions, concurrence = {} } = alert || {};
	const [dynamicKVPWidgets, setDynamicKVPWidgets] = useState([]);
	const metadata = object?.metadata;
	const externalSystemId = object?.externalSystemId;
	const closed = forReplay ? alert?.transactionClosed : alert?.closed;
	const type = eventLookups?.types?.find(item => item.id === eventTypeId);
	const isEvent = object?.entity?.entityType === "event";
	const canEnableEvent = isEvent && !eventEnabled && object.entity.disabled === true;

	const [selectedDateTime, setSelectedDateTime] = useState(activityDate || createdDate);

	const intervalRef = useRef(null);
	const alarmSummaryWidgetRef = useRef(null);

	useEffect(() => {
		dispatch(startListStream(notificationId, "alert", "dock"));
	}, []);

	useEffect(() => {
		if (target?.entityType === "device" && target.entityData?.properties?.features?.length > 0) {
			setDeviceWithControls(target);
		}
	}, [target]);

	useEffect(() => {
		if (activityObjectFeed?.widgets) {
			const dynamicWidgets = activityObjectFeed.widgets.filter(widget => {
				if (widget.id === "dynamic-kvp") {
					if (widget.condition) {
						try {
							// Evaluate the JSONata expression against entity.activity
							const expression = jsonata(widget.condition);
							return expression.evaluate(entity.activity);
						} catch (error) {
							console.error("Error evaluating JSONata expression:", error);
							return false;
						}
					}
					return true;
				}
				return false;
			});
			setDynamicKVPWidgets(dynamicWidgets);
		}
	}, [activityObjectFeed, entity.activity]);

	useEffect(() => {
		if (context?.entity) {
			if (context.entity.closed) {
				closeDetailView();
			} else {
				setAlert({ ...(context.entity || {}) });

				if (context?.entity?.summary && alarmSummaryWidgetRef.current) {
					alarmSummaryWidgetRef.current.setSelectedSummaryCodes({ ...context.entity.summary });
				}
			}
		}
	}, [context?.entity]);

	useEffect(() => {
		const enabled =
			!closed &&
			(appId === "map-app" || appId === "events-app") &&
			!alert?.escalationDisabled &&
			!readOnly &&
			canEnableEvent &&
			canManageEvent;

		setCanEscalateAlert(enabled);
	}, [closed, appId, alert, readOnly, canEnableEvent, canManageEvent]);

	useEffect(() => {
		const enabled =
			!closed &&
			(appId === "map-app" || appId === "events-app") &&
			!alert?.escalationDisabled &&
			!readOnly &&
			canManageEvent;

		setCanEscalateEvent(enabled);
	}, [closed, appId, alert, readOnly, canManageEvent]);

	const isAlertConcurrenceEnabled = useSelector(selectIsAlertConcurrenceEnabled);

	const cameraList = uniq([...((isArray(camerasInRange) && camerasInRange) || [])]);

	const handleCreateEscalate = callback => {
		const event = {
			name: alert?.message,
			startDate: alert?.createdDate,
			id: alert?.id,
			isAlert: !!alert?.eventTypeId
		};
		const body = JSON.stringify(event);
		eventService.escalateEvent(body, callback);
	};

	const handleEventEscalate = () => {
		handleCreateEscalate((err, res) => {
			if (err) {
				console.log(err);
			} else {
				dispatch(loadProfile(res.id, res.name, res.entityType, "profile"));
			}
		});
	};
	const handleEnableEvent = () => {
		eventService.enableEvent(alert?.activity?.object?.id, (err, response) => {
			if (err) {
				console.log(err, response);
			} else {
				setEventEnabled(true);
			}
		});
	};
	const [hasRequestedConcurrence, setHasRequestedConcurrence] = useState(false);

	const validateSummaryCodes = () => {
		if (!alarmSummaryWidgetRef.current) return true;

		let errors = {};
		// Validate required summary codes
		alarmSummaryWidgetRef.current.getSummaryCodes().forEach(code => {
			const selectedSummaryCodes = alarmSummaryWidgetRef.current.getSelectedSummaryCodes();
			if (code.required && !selectedSummaryCodes[code.id]) {
				errors[code.id] = true;
			}
		});

		// If any required summary code is missing, block and show errors
		if (Object.keys(errors).length > 0) {
			alarmSummaryWidgetRef.current.setSummaryCodeErrors(errors);
			return false;
		}
		alarmSummaryWidgetRef.current.setSummaryCodeErrors({});
		return true;
	};

	const handleConcurrenceRequestButton = () => {
		if (alert?.occurrenceCount !== alert?.ackedCount || hasRequestedConcurrence) return;

		if (!validateSummaryCodes()) return;

		setHasRequestedConcurrence(true);
		alertService.requestConcurrence(alert.id, async (err, response) => {
			setHasRequestedConcurrence(false);

			if (err) {
				const errorData = (await err.response?.json()) || {};
				console.error("Failed:", {
					status: err.response?.status,
					message: errorData.message || "Request failed"
				});
				dispatch(
					createUserFeedback(
						getTranslation("global.profiles.widgets.alarmDetail.unableToRequestConcurrence"),
						null,
						true // this marks it as an error
					)
				);

				return;
			}
		});
	};
	const handleAcknowledge = () => {
		const isCurrentlyAcked = selectedOccurrence?.acknowledged;

		const alertPayload =
			occurrences.length > 1
				? [{ alertId: alert.id, occurrenceId: selectedOccurrence?.id }]
				: [{ alertId: alert.id, occurrenceId: "*" }];

		if (isCurrentlyAcked) {
			if (alert?.eventPriorityId) {
				dispatch(unAcknowledgeAlerts(alertPayload));
			} else {
				dispatch(unAcknowledgeNotifications(alertPayload));
			}
		} else {
			if (alert?.eventPriorityId) {
				dispatch(acknowledgeAlerts(alertPayload));
				EventBus.publish("getAcknowledgedAlerts", {
					alertsData: alert,
					ackAll: false
				});
			} else {
				dispatch(acknowledgeNotifications(alertPayload));
			}
		}
	};

	useEffect(() => {
		if (readOnly) return;
		if (isAutoAckDisabled !== false) return;
		if (selectedOccurrence?.acknowledged) return; // Don't auto-ack if already acknowledged

		handleAcknowledge();
	}, [readOnly, isAutoAckDisabled]);

	const handleClose = () => {
		if (!validateSummaryCodes()) return;
		const { id } = alert;
		EventBus.publish("closeAlerts", [id]);
		dispatch(closeNotification(id, alert?.eventPriorityId ? "alertService" : "notificationService"));
		closeDetailView();
	};

	const streamVideoWallClusterCameras = cameras => {
		cameras?.forEach(innerArray => {
			innerArray?.forEach(camera => {
				if (camera?.cameraId) {
					alertCameraIds[alert?.id] = [...new Set([...(alertCameraIds[alert?.id] || []), camera?.cameraId])];
					dispatch(streamIndividualEntityByType(camera.cameraId, "camera", "dock"));
				}
			});
		});
	};

	const handleConcurrenceResponse = approved => {
		if (isResponding) return;
		alertService.respondToConcurrence(alert.id, { approved }, (err, response) => {
			if (err) {
				console.error("Failed to respond to concurrence:", err);
				dispatch(
					createUserFeedback(
						getTranslation("global.profiles.widgets.alarmDetail.unableToRespond"),
						null,
						true
					)
				);
			} else {
				handleClose();
			}
			if (!approved) {
				setAlert({ ...alert, concurrence: { ...concurrence, request: undefined } });
				setHasRequestedConcurrence(false); // Allow new request
			}
		});
	};

	const styles = {
		card: {
			borderRadius: 0,
			display: "flex",
			flexDirection: "column",
			height: "100%",
			background: alertProfileSlideout ? "#343538" : "#2C2D2F"
		},
		cardHeader: {
			backgroundColor: alertProfileSlideout ? "#343538" : "#262729",
			color: "#fff",
			alignItems: "flex-start",
			padding: "4px 8px",
			flexWrap: "wrap"
		},
		alertActions: {
			display: "flex",
			backgroundColor: "#262729",
			padding: "0px 0px 11px",
			...(alertProfileSlideout && { background: "#343538", borderBottom: "1px solid #46484B" }),
			...(isExpanded && { paddingBottom: 0 })
		},
		scrollContainer: {
			backgroundColor: alertProfileSlideout ? "#343538" : "#2C2D2F",
			overflow: "scroll"
		},
		widgetTitle: {
			color: "#fff",
			lineHeight: "48px",
			...(dir === "rtl" ? { marginRight: 12 } : { marginLeft: 12 })
		},
		spriteIcon: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: 10,
			borderRadius: 5,
			backgroundColor: colors[type?.color]?.color ? colors[type?.color]?.color : "#979798",
			height: 25,
			width: 25,
			...(dir === "rtl" ? { marginRight: 11 } : { marginLeft: 11 })
		},
		closeIconForExpanded: {
			fontSize: 22,
			...(dir === "rtl" ? { marginRight: 20 } : { marginLeft: 20 })
		},
		actions: {
			...(isExpanded && {
				display: "flex",
				alignItems: "center",
				height: "100%",
				...(dir === "rtl" ? { paddingRight: 10 } : { paddingLeft: 10 })
			})
		},
		menuItem: {
			paddingLeft: 0,
			marginLeft: 0,
			marginLeft: 5
		},
		clustersDropDownControl2: {
			width: "150",
			fontSize: 12,
			marginLeft: 0,
			paddingLeft: 0,
			marginBottom: 4,
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start"
		},
		dropDownMenuPaperProps: {
			backgroundColor: "#2C2D2F",
			color: "white",
			marginTop: "-38px",
			fontSize: 12,
			// boxShadow: "none",
			// border: "none",
			paddingLeft: 0,
			marginLeft: 0
		}
	};

	const closeIcon = (
		<Close
			style={{
				color: "#FFF",
				cursor: "pointer",
				fontSize: 18,
				marginRight: 10,
				...(isExpanded && styles.closeIconForExpanded)
			}}
			onClick={() => closeDetailView()}
		/>
	);

	const profileActions = (
		<div style={{ display: "flex", alignItems: "center", margin: "2.5px 0" }}>
			<div style={styles.actions}>
				{alert?.occurrenceCount === alert?.ackedCount &&
					isAlertConcurrenceEnabled &&
					!hasRequestedConcurrence &&
					!concurrence?.request && (
						<Button
							style={{
								height: 20,
								color: "#fff",
								width: 131,
								padding: 0,
								fontSize: 10,
								lineHeight: "13px",
								margin: "0px 5px",
								textTransform: "uppercase"
							}}
							sx={{
								"&:hover": {
									backgroundColor: "#85C7EF !important"
								}
							}}
							onClick={handleConcurrenceRequestButton}
							variant="contained"
							color="primary"
						>
							<Translate value={"global.profiles.widgets.alarmDetail.requestConcurrence"} />
						</Button>
					)}
				{isAlertConcurrenceEnabled && concurrence?.request && (
					<>
						{concurrence.request.userId === user.profile.id || !canRespondToConcurrence ? (
							// Requestor (User 1) sees "Pending Concurrence"
							<Button
								style={{
									height: 20,
									width: 131,
									fontSize: 10,
									opacity: 0.5,
									lineHeight: "13px",
									margin: "0px 5px",
									backgroundColor: "#343538",
									color: "#B5B9BE",
									border: "1px solid #707070",
									textTransform: "uppercase"
								}}
								variant="contained"
								disabled
							>
								<span
									style={{
										width: 120,
										height: 13,
										fontSize: 10,
										lineHeight: "13px",
										color: "#FFFFFF",
										whiteSpace: "nowrap"
									}}
								>
									<Translate value="global.profiles.widgets.alarmDetail.pendingConcurrence" />
								</span>
							</Button>
						) : allowAlertConcurrence ? (
							<>
								<span
									style={{
										height: 16,
										color: "#fff",
										width: 50,
										padding: 0,
										fontSize: 12,
										lineHeight: "16px",
										margin: "0px 5px"
									}}
								>
									<Translate value={"global.profiles.widgets.alarmDetail.approve"} />
								</span>
								<Button
									style={{
										height: 20,
										color: "#fff",
										width: 30,
										padding: 0,
										fontSize: 10,
										lineHeight: "13px",
										margin: "0px 5px",
										textTransform: "uppercase"
									}}
									sx={{
										minWidth: "auto",
										px: 0.5,
										"&:hover": {
											backgroundColor: "#85C7EF !important"
										}
									}}
									onClick={() => handleConcurrenceResponse(true)}
									variant="contained"
									color="primary"
									disabled={isResponding}
								>
									<Translate value={"global.profiles.widgets.alarmDetail.yes"} />
								</Button>
								<Button
									style={{
										height: 20,
										color: "#fff",
										width: 30,
										padding: 0,
										fontSize: 10,
										lineHeight: "13px",
										margin: "0px 5px",
										textTransform: "uppercase"
									}}
									sx={{
										minWidth: "auto",
										px: 0.5,
										"&:hover": {
											backgroundColor: "#85C7EF !important"
										}
									}}
									onClick={() => handleConcurrenceResponse(false)}
									variant="contained"
									color="primary"
									disabled={isResponding}
								>
									<Translate value={"global.profiles.widgets.alarmDetail.no"} />
								</Button>
							</>
						) : (
							// Users with allowAlertConcurrence=false see "Pending Review"
							<Button
								style={{
									height: 20,
									width: 131,
									fontSize: 10,
									opacity: 0.5,
									lineHeight: "13px",
									margin: "0px 5px",
									backgroundColor: "#343538",
									color: "#B5B9BE",
									border: "1px solid #707070",
									textTransform: "uppercase"
								}}
								variant="contained"
								disabled
							>
								<span
									style={{
										width: 120,
										height: 13,
										fontSize: 10,
										lineHeight: "13px",
										color: "#FFFFFF",
										whiteSpace: "nowrap"
									}}
								>
									<Translate value="global.profiles.widgets.alarmDetail.pendingReview" />
								</span>
							</Button>
						)}
					</>
				)}
				{/* Acknowledged and unack not disabled */}
				{selectedOccurrence?.acknowledged && !disableAlertUnack && (
					<Button
						style={{
							height: 20,
							color: "#fff",
							width: 57,
							padding: 0,
							fontSize: 12,
							margin: "0px 5px",
							backgroundColor: "unset",
							border: "1px solid white"
						}}
						sx={{
							"&:hover": {
								backgroundColor: "#5E6265 !important"
							}
						}}
						onClick={() => handleAcknowledge()}
						variant="contained"
						color="primary"
					>
						<Translate value={"global.profiles.widgets.alarmDetail.unAck"} />
					</Button>
				)}
				{/* Not acknowledged */}
				{!selectedOccurrence?.acknowledged && (
					<Button
						style={{
							height: 20,
							color: "#fff",
							width: 57,
							padding: 0,
							fontSize: 12,
							margin: "0px 5px"
						}}
						sx={{
							"&:hover": {
								backgroundColor: "#85C7EF !important"
							}
						}}
						onClick={() => handleAcknowledge()}
						variant="contained"
						color="primary"
					>
						<Translate value={"global.profiles.widgets.alarmDetail.ack"} />
					</Button>
				)}
				{!isAlertConcurrenceEnabled && (
					<Button
						style={{
							height: 20,
							color: "#fff",
							width: 57,
							padding: 0,
							fontSize: 12,
							margin: "0px 5px",
							backgroundColor: "unset",
							border: "1px solid white"
						}}
						sx={{
							"&:hover": {
								backgroundColor: "#5E6265 !important"
							}
						}}
						onClick={() => handleClose()}
						variant="contained"
						color="primary"
					>
						<Translate value="global.profiles.widgets.alarmDetail.dismiss" />
					</Button>
				)}
			</div>
			{isExpanded && !disableCloseIcon && closeIcon}
		</div>
	);

	const unSubscribeCameras = () => {
		const contextId = selectedContext?.entity?.id;
		if (!contextId || !alertCameraIds[contextId]) return;

		// Avoid using variables that rely on `useSelector` inside unmount logic, as their values may be stale or inaccessible.
		// Use `window.redux.getState()` to imperatively access the latest Redux state during cleanup.

		const state = window.redux.getState();
		const contextData = state.contextualData;

		// Helper to count how many times a cameraId appears in all alerts
		const getCameraIdCount = cameraId =>
			Object.values(alertCameraIds).reduce((count, ids) => {
				return count + (new Set(ids).has(cameraId) ? 1 : 0);
			}, 0);

		// This function ensures that cameras linked to a specific alert context are unsubscribed only if no other alerts use them.
		// It checks each camera's usage across all alerts and unsubscribes only if the camera is exclusive to the current context.
		// Regardless of usage, it removes the camera ID from the local alertCameraIds cache for cleanup.

		alertCameraIds[contextId].forEach(cameraId => {
			const cameraContext = contextData[cameraId];
			if (!cameraContext) return;

			const subscriptions = [].concat(cameraContext?.subscriptions?.entity?.subscription || []);
			if (!subscriptions.length) return;

			const isOnlyUsedHere = getCameraIdCount(cameraId) === 1;

			if (isOnlyUsedHere) {
				subscriptions.forEach(sub => {
					if (sub !== "shared") {
						dispatch(unsubscribeFromContext(cameraId, "dock"));
						dispatch(unsubscribe(sub));
					}
				});
			}

			// Always remove from local cache for this alert
			const updatedIds = alertCameraIds[contextId].filter(id => id !== cameraId);
			updatedIds.length ? (alertCameraIds[contextId] = updatedIds) : delete alertCameraIds[contextId];
		});
	};

	useEffect(() => {
		if (alert?.id && isAlertConcurrenceEnabled && alert?.concurrence?.request) {
			alertService.canUserProvideConcurrence(alert.id, (err, response) => {
				if (err) {
					console.error("Error checking concurrence permission:", err);
					return;
				}
				if (response?.success == false) {
					setCanRespondToConcurrence(false);
				} else {
					setCanRespondToConcurrence(true);
				}
			});
		}
	}, [alert, isAlertConcurrenceEnabled]);

	useEffect(() => {
		return () => {
			unSubscribeCameras();
		};
	}, []);

	useEffect(() => {
		updateAlertDuration();

		// refreshing alert duration every minute only if the alert is created within 24 hours.
		const daysPassed = moment().diff(activityDate || createdDate, "days");
		if (!daysPassed) {
			intervalRef.current = setInterval(() => {
				updateAlertDuration();
			}, [60000]);
		}

		return () => {
			clearInterval(intervalRef.current);
		};
	}, [alert?.id]);

	const updateAlertDuration = () => {
		const formattedDateTime = moment(activityDate || createdDate).format("YYYY/MM/DD HH:mm:ss");
		const endTime = moment();
		const timeDiff = endTime.diff(moment(formattedDateTime, "YYYY/MM/DD HH:mm:ss"));
		setMinutesPassed(moment.utc(timeDiff).locale("en").format("m"));
		setHoursPassed(endTime.diff(activityDate || createdDate, "hours"));
		setDaysPassed(endTime.diff(activityDate || createdDate, "days"));
	};
	const getDurationLabel = dateTime => {
		const endTime = moment();
		const days = endTime.diff(dateTime, "days");
		if (days) {
			return getTranslation(`global.profiles.widgets.alarmDetail.ago.day${days > 1 ? "s" : ""}`, days);
		}

		const timeDiff = endTime.diff(moment(dateTime));
		const minutes = Number(moment.utc(timeDiff).locale("en").format("m"));
		const hours = endTime.diff(dateTime, "hours");

		return hours
			? getTranslation(`global.profiles.widgets.alarmDetail.ago.hour`, hours, minutes)
			: minutes > 0
				? getTranslation(`global.profiles.widgets.alarmDetail.ago.minute`, minutes)
				: getTranslation("global.profiles.widgets.alarmDetail.ago.moments");
	};
	const alertDuration = daysPassed
		? getTranslation(`global.profiles.widgets.alarmDetail.ago.day${daysPassed > 1 ? "s" : ""}`, daysPassed)
		: hoursPassed
			? getTranslation(`global.profiles.widgets.alarmDetail.ago.hour`, hoursPassed, minutesPassed)
			: minutesPassed > 0
				? getTranslation(`global.profiles.widgets.alarmDetail.ago.minute`, minutesPassed)
				: getTranslation("global.profiles.widgets.alarmDetail.ago.moments");

	const renderEscalateButtons = () => {
		return (
			<div style={styles.alertActions}>
				<div
					style={{
						width: "100%",
						display: "flex",
						alignItems: "center",
						paddingLeft: isBottomDock ? 0 : 10
					}}
				>
					<div style={{ flexGrow: 1 }}>
						{canEscalateEvent && (
							<Button
								style={{
									height: 20,
									color: "#fff",
									width: 108,
									padding: 0,
									fontSize: 11,
									margin: "0px 0px"
								}}
								sx={{
									"&:hover": {
										backgroundColor: "#85C7EF !important"
									}
								}}
								onClick={() => handleEventEscalate()}
								variant="contained"
								color="primary"
							>
								<Translate value="global.profiles.widgets.alarmDetail.activateResponse" />
							</Button>
						)}
						{canEscalateAlert && (
							<Button
								style={{
									height: 20,
									color: "#fff",
									width: 108,
									padding: 0,
									fontSize: 11,
									margin: "0px 0px"
								}}
								sx={{
									"&:hover": {
										backgroundColor: "#85C7EF !important"
									}
								}}
								onClick={() => handleEnableEvent()}
								variant="contained"
								color="primary"
							>
								<Translate value="global.profiles.widgets.alarmDetail.activateResponse" />
							</Button>
						)}
					</div>
					{!readOnly && !isExpanded ? profileActions : null}
				</div>
			</div>
		);
	};

	return alert?.id ? (
		<Card style={styles.card}>
			{!alertProfileSlideout && !isExpanded && (
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						backgroundColor: "#1D1D1D",
						paddingTop: 13,
						paddingBottom: 7,
						height: 44
					}}
				>
					<div
						style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
						onClick={() => {
							closeDetailView();
						}}
					>
						<i className="material-icons" style={{ marginLeft: 10, color: "#FFF" }}>
							{dir === "rtl" ? "keyboard_arrow_right" : "keyboard_arrow_left"}
						</i>
						<div style={{ color: "#FFF", fontSize: 15, marginLeft: 9 }}>
							<Translate value="global.profiles.widgets.alarmDetail.back" />
						</div>
					</div>

					{!disableCloseIcon && (
						<Close
							style={{ cursor: "pointer", fontSize: 18, marginRight: 10 }}
							sx={{ color: "#FFF" }}
							onClick={() => {
								closeDetailView();
							}}
						/>
					)}
				</div>
			)}
			<CardHeader
				sx={{
					...styles.cardHeader,
					"& .MuiCardHeader-content": {
						width: isBottomDock ? "30%" : "50%"
					}
				}}
				avatar={
					<div
						style={{
							display: "flex",
							alignItems: "flex-start"
						}}
					>
						{geometry ? (
							<TargetingIcon
								geometry={geometry}
								defaultVisibility={true}
								iconContainerStyle={{ padding: 0, marginTop: 2 }}
							/>
						) : null}

						<div style={styles.spriteIcon}>
							<SpriteIcon
								iconName={type?.icon || "icon_information"}
								customHeight="17.5px"
								customWidth="17.5px"
							/>
						</div>
					</div>
				}
				title={
					<div
						style={{
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap"
						}}
					>
						<Typography
							variant="inherit"
							style={{
								lineHeight: "12px",
								fontSize: "12px",
								color: "#FFF"
							}}
						>
							{alert?.message}
						</Typography>

						{alertCount > 1 && (
							<>
								<Typography variant="inherit" style={{ margin: "0px 5px" }} color="#82878C">
									|
								</Typography>
								<Typography variant="inherit" color="#82878C">
									<Translate
										value="global.profiles.widgets.alarmDetail.aggregateCount"
										count={alertCount}
									/>
								</Typography>
							</>
						)}
					</div>
				}
				titleTypographyProps={{
					style: {
						lineHeight: "12px",
						fontSize: "12px"
					}
				}}
				subheader={
					<Fragment>
						{Boolean(alert.aggregateState) && occurrences.length > 1 ? (
							<div style={styles.clustersDropDownControl2}>
								<CustomizedTextField
									variant="outlined"
									name="dateTime"
									value={selectedOccurrenceId}
									onChange={e => {
										const selectedOcc = occurrences.find(occ => occ.id === e.target.value);
										setSelectedDateTime(selectedOcc?.createdDate || alert.createdDate);
										setSelectedOccurrenceId(e.target.value);
									}}
									width={180}
									height={40}
									select
									SelectProps={{
										MenuProps: {
											disablePortal: true,
											getContentAnchorEl: null, // Prevents default anchoring behavior
											anchorOrigin: {
												vertical: "bottom",
												horizontal: "left"
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left"
											},
											PaperProps: {
												className: "aggregate-alert-menu-paper",
												sx: styles.dropDownMenuPaperProps
											}
										}
									}}
								>
									{orderedOccurrences?.map((occurrence, index) => {
										const dateTime = occurrence.createdDate;
										const itemDuration = getDurationLabel(dateTime);
										return (
											<MenuItem
												value={occurrence.id}
												key={occurrence.id}
												disableGutters
												sx={{
													paddingLeft: 0,
													paddingRight: 0,
													fontSize: "10px" // 2px less than 12px
												}}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "row",
														alignItems: "flex-start",
														width: "100%",
														marginRight: 0
													}}
												>
													<span
														style={{
															display: "inline-block",
															textAlign: "right",
															color: "#B5B9BE",
															fontSize: "10px",
															marginLeft: 5,
															marginTop: 1,
															marginRight: 5
														}}
													>
														{index + 1}.
													</span>

													{isBottomDock ? (
														<div
															style={{
																display: "flex",
																flexDirection: "column",
																flex: 1
															}}
														>
															<div
																style={{
																	display: "flex",
																	marginTop: "3px",
																	fontSize: "10px",
																	lineHeight: "11px"
																}}
															>
																<Typography
																	color="#82878C"
																	variant="inherit"
																	style={{ fontSize: "10px" }}
																>
																	{timeConversion.convertToUserTime(
																		occurrence.createdDate,
																		`full_${timeFormat} `,
																		locale
																	)}
																</Typography>
															</div>
															<div
																style={{
																	display: "flex",
																	marginTop: "0px",
																	fontSize: "10px",
																	lineHeight: "11px"
																}}
															>
																<Typography
																	color="#82878C"
																	variant="inherit"
																	style={{ fontSize: "10px" }}
																>
																	{itemDuration}
																</Typography>
															</div>
															<div
																style={{
																	display: "flex",
																	marginTop: "0px",
																	marginBottom: "3px",
																	fontSize: "10px",
																	lineHeight: "11px"
																}}
															>
																{occurrence?.acknowledged ? (
																	<Typography
																		style={{ fontSize: "10px", lineHeight: "11px" }}
																		color="#82878C"
																	>
																		<Translate
																			value={
																				"global.profiles.widgets.alarmDetail.acknowledgedBy"
																			}
																			count={
																				occurrence?.acknowledgedByUserName ||
																				occurrence?.acknowledgedBy
																			}
																		/>
																	</Typography>
																) : (
																	<Typography
																		style={{ fontSize: "10px", lineHeight: "11px" }}
																		color="#82878C"
																	>
																		<Translate
																			value={
																				"global.profiles.widgets.alarmDetail.notAcknowledged"
																			}
																		/>
																	</Typography>
																)}
															</div>
														</div>
													) : (
														<div
															style={{
																display: "flex",
																flexDirection: "column",
																flex: 1
															}}
														>
															<div
																style={{
																	display: "flex",
																	marginTop: "3px",
																	fontSize: "10px",
																	lineHeight: "11px"
																}}
															>
																<Typography
																	color="#82878C"
																	variant="inherit"
																	style={{ fontSize: "10px" }}
																>
																	{timeConversion.convertToUserTime(
																		occurrence.createdDate,
																		`full_${timeFormat} `,
																		locale
																	)}
																</Typography>
																<Typography
																	style={{ margin: "0px 5px" }}
																	color="#82878C"
																	variant="inherit"
																>
																	|
																</Typography>
																<Typography
																	color="#82878C"
																	variant="inherit"
																	style={{ fontSize: "10px" }}
																>
																	{itemDuration}{" "}
																</Typography>
															</div>
															<div
																style={{
																	display: "flex",
																	marginTop: "0px",
																	marginBottom: "3px",
																	fontSize: "10px",
																	lineHeight: "11px"
																}}
															>
																{occurrence?.acknowledged ? (
																	<Typography
																		style={{ fontSize: "10px", lineHeight: "11px" }}
																		color="#82878C"
																	>
																		<Translate
																			value={
																				"global.profiles.widgets.alarmDetail.acknowledgedBy"
																			}
																			count={
																				occurrence?.acknowledgedByUserName ||
																				occurrence?.acknowledgedBy
																			}
																		/>
																	</Typography>
																) : (
																	<Typography
																		style={{ fontSize: "10px", lineHeight: "11px" }}
																		color="#82878C"
																	>
																		<Translate
																			value={
																				"global.profiles.widgets.alarmDetail.notAcknowledged"
																			}
																		/>
																	</Typography>
																)}
															</div>
														</div>
													)}
												</div>
											</MenuItem>
										);
									})}{" "}
								</CustomizedTextField>
							</div>
						) : isBottomDock ? (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									flex: 1
								}}
							>
								{" "}
								<div
									style={{
										display: "flex",
										marginTop: "3px",
										fontSize: "10px",
										lineHeight: "11px"
									}}
								>
									<Typography color="#82878C" variant="inherit">
										{timeConversion.convertToUserTime(
											activityDate || createdDate,
											`full_${timeFormat} `,
											locale
										)}
									</Typography>
								</div>
								<div
									style={{
										display: "flex",
										marginTop: "0px",
										fontSize: "10px",
										lineHeight: "11px"
									}}
								>
									<Typography color="#82878C" variant="inherit">
										{alertDuration}
									</Typography>
								</div>
							</div>
						) : (
							<div
								style={{
									display: "flex",
									marginTop: "3px",
									fontSize: "10px",
									lineHeight: "11px"
								}}
							>
								<Typography color="#82878C" variant="inherit">
									{timeConversion.convertToUserTime(
										activityDate || createdDate,
										`full_${timeFormat} `,
										locale
									)}
								</Typography>
								<Typography style={{ margin: "0px 5px" }} color="#82878C" variant="inherit">
									|
								</Typography>
								<Typography color="#82878C" variant="inherit">
									{alertDuration}
								</Typography>
							</div>
						)}
						{occurrences.length <= 1 &&
						selectedOccurrence?.acknowledgedBy &&
						selectedOccurrence?.acknowledgedBy != "null" ? (
							<Typography style={{ fontSize: "10px", lineHeight: "11px" }} color="#82878C">
								<Translate
									value={"global.profiles.widgets.alarmDetail.acknowledgedBy"}
									count={
										selectedOccurrence?.acknowledgedByUserName || selectedOccurrence?.acknowledgedBy
									}
								/>
							</Typography>
						) : null}
					</Fragment>
				}
				subheaderTypographyProps={{
					component: "div",
					styles: {
						color: "#fff"
					}
				}}
				action={
					alertProfileSlideout ? (
						closeIcon
					) : isExpanded ? (
						<div style={{ display: "flex", alignItems: "center", height: "25px" }}>
							{renderEscalateButtons()}
							{profileActions}
						</div>
					) : null
				}
				classes={{
					avatar: dir === "rtl" ? classes.avatar : {},
					action: isExpanded && classes.cardHeaderForExpanded
				}}
			/>
			{!isBottomDock && renderEscalateButtons()}
			<div style={styles.scrollContainer}>
				{actions?.length > 0 && (
					<>
						<AlertActionsWidget
							actions={actions}
							alertTime={alert.createdDate}
							alertId={alert.id}
							alertAcknowledged={alert?.occurrenceCount === alert?.ackedCount}
						/>
						<Divider style={{ backgroundColor: "#46484B" }} />
					</>
				)}

				{dynamicKVPWidgets?.length > 0 &&
					dynamicKVPWidgets.map((widget, index) => {
						const { id, columns, title, properties } = widget;
						return (
							<Fragment key={`${id}-${index}`}>
								<DynamicKVPWidget
									title={title}
									columns={columns}
									propertiesConfig={properties}
									entity={entity.activity}
								/>
								<Divider style={{ backgroundColor: "#46484B" }} />
							</Fragment>
						);
					})}

				{alert?.cameraClusters?.clusters?.length > 0 ||
				entity?.cameraClusters?.clusters?.length > 0 ||
				entity?.activity?.target?.cameraClusters?.clusters?.length > 0 ? (
					<CameraClusters
						key={notificationId}
						isOnAlertProfile={true}
						streamVideoWallClusterCameras={streamVideoWallClusterCameras}
						selectedDateTime={selectedDateTime}
						contextId={notificationId}
						readOnly={true}
					/>
				) : (
					<>
						<div style={styles.widgetTitle}>
							<Translate value="global.profiles.widgets.alarmDetail.cameras" />
						</div>
						<div style={{ padding: "0px 13px 0px 13px" }}>
							<CamerasWidget
								key={notificationId}
								otherCameras={cameraList}
								contextId={activityId}
								geometry={geometry}
								readOnly={readOnly}
								subscriberRef="dock"
								fullscreenCamera={fullscreenCamera}
								disableSlew={!geometry}
								disableLock={true}
								isAlertProfile={true}
								selectFloorPlanOn={selectFloorPlanOn}
								floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
								alertDateTime={activityDate || createdDate}
							/>
						</div>
					</>
				)}
				{metadata?.sensorop && (
					<>
						<div style={styles.widgetTitle}>
							<Translate value="global.profiles.widgets.alarmDetail.sensorOPDetection" />
						</div>
						<div style={{ padding: "0px 13px 0px 13px" }}>
							<SensorOPDetectionWidget
								key={notificationId}
								sensorop={metadata.sensorop}
								metadata={alert?.metadata}
								alertId={alert?.id}
								externalSystemId={externalSystemId}
							/>
						</div>
						<Divider style={{ backgroundColor: "#46484B" }} />
					</>
				)}
				{metadata?.cardholder && (
					<>
						<div style={styles.widgetTitle}>
							<Translate value="global.profiles.widgets.alarmDetail.cardHolder" />
						</div>
						<div style={{ padding: "0px 13px 0px 13px" }}>
							<CardHolderWidget
								key={notificationId}
								cardholder={metadata?.cardholder}
								filter={object?.filter}
								dir={dir}
							/>
						</div>
						<Divider style={{ backgroundColor: "#46484B" }} />
					</>
				)}
				{devicePointWithControls ? (
					<>
						<AccessControlWidget
							key={notificationId}
							id="access_control"
							contextId={devicePointWithControls.id}
							enabled={true}
							isForAlertProfile={true}
							deviceData={devicePointWithControls}
						/>
						<Divider style={{ backgroundColor: "#46484B" }} />
					</>
				) : null}

				{context?.attachments?.length > 0 && (
					<>
						<Divider style={{ backgroundColor: "#46484B" }} />
						<div style={{ padding: "0px 13px 0px 13px" }}>
							<FileWidget
								key={notificationId}
								contextId={notificationId}
								readOnly={readOnly}
								canDelete={!readOnly}
								subscriberRef="dock"
								isAlertProfile={true}
							/>
						</div>
					</>
				)}

				<AlarmSummaryWidget
					ref={alarmSummaryWidgetRef}
					alert={alert}
					disabled={Boolean(alert?.concurrence?.request)}
					forReplay={forReplay}
					readOnly={readOnly}
				/>

				<Divider style={{ backgroundColor: "#46484B" }} />
				{notificationId && (
					<>
						<div style={styles.widgetTitle}>
							<Translate value="global.profiles.widgets.alarmDetail.timeline" />
						</div>
						<div style={{ padding: "0px 13px 0px 13px" }}>
							<Activities
								key={notificationId}
								contextId={notificationId}
								subscriberRef="dock"
								readOnly={readOnly}
								isAlertProfile={true}
								forReplay={forReplay}
								endDate={endDate}
								profile="alert"
							/>
						</div>
						<Divider style={{ backgroundColor: "#46484B" }} />
					</>
				)}
				<div style={styles.widgetTitle}>
					<Translate value="global.profiles.widgets.alarmDetail.pinnedItems" />
				</div>
				<div style={{ padding: "0px 13px 0px 13px" }}>
					<PinnedItemsWidget
						key={notificationId}
						contextId={activityId}
						readOnly={readOnly}
						subscriberRef="dock"
						isAlertProfile={true}
						forReplay={forReplay}
						notificationId={notificationId}
					/>
				</div>
				<Divider style={{ backgroundColor: "#46484B" }} />
				<ListWidget
					id={notificationId}
					expanded={false}
					readOnly={readOnly}
					listAccessAndEventsManage={!readOnly && canManageLists}
					contextId={notificationId}
					subscriberRef={"dock"}
					widgetsExpandable={false}
					defaultEnabled={true}
					targetType={"alert"}
					canModifyListRows={true}
					disableWidgetSettings={true}
				/>
			</div>
		</Card>
	) : (
		<></>
	);
};

AlertPanelDetailWidget.propTypes = propTypes;
AlertPanelDetailWidget.defaultProps = defaultProps;

export default memo(AlertPanelDetailWidget);
